import qs from 'querystring';

import { nanoid } from 'nanoid';

import utmz from '@sravni/server-utils/lib/utmz';
import type { IAdvertising } from '@sravni/types/lib/advertising';
import type IFreeButtonDialogAction from '@sravni/vitrina/lib/types/config/IFreeButtonDialogAction';
import type IFreeButtonLinkAction from '@sravni/vitrina/lib/types/config/IFreeButtonLinkAction';
import type IProduct from '@sravni/vitrina/lib/types/IProduct';

import { MONETIZATION_TYPES } from '../constants/advertising';
import type OfferTypes from '../enums/offerTypes';
import ProductTypes from '../enums/productTypes';
import SourceTypes from '../enums/SourceTypes';
import { getGaIdFromCookies, getYandexMetricsId } from '../utils/analytics/analytics';
import { itemLabelFactory } from '../utils/analytics/events';

const KEYS = {
  SUB: 'adv_sub',
  SOURCE: 'source',
  PATH: 'aff_sub2',
  UTM_LABELS: 'aff_sub3',
  PRODUCT: 'aff_sub4',
  TYPE: 'aff_sub5',
  GAID: 'aff_sub',
  PRODUCT_LINK: 'aff_unique3',
  URL: 'url',
  USER_ID: 'aff_unique1',
  FILTERS: 'filters',
};

export interface IPixelHandleEventProps {
  advertising: IAdvertising;
  product?: IProduct;
  offerType: OfferTypes;
  productName: string;
  productSubName: string;
  eventCategory: string;
  isAdvertisingCommon: boolean;
  productLink: string;
  organizationName: string;
  source?: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
  productId?: string;
  productType?: string;
  filters?: Record<string, any>;
  freeButtonAction: IFreeButtonDialogAction | IFreeButtonLinkAction;
  onEvent: () => void;
}

export interface IMakePixelProps {
  sub: string;
  product?: IProduct;
  type?: string;
  source?: string;
  cookie?: Record<string, any>;
  pathname?: string;
  productLink?: string;
  userId?: string;
  isWL?: boolean;
  isConversion?: boolean;
  kind?: string;
  productId?: string;
  productType?: string;
  cookies?: {
    utmz?: string;
    ym?: string;
    ga?: string;
    ab?: string;
  };
  filters?: Record<string, any>;
  paymentNetwork?: string;
}

interface IUTMZParams {
  url?: string;
  utmzCookie?: string;
}

const getUTMZString = ({ url, utmzCookie }: IUTMZParams): null | string => {
  if (utmzCookie) {
    return utmzCookie;
  }

  if (url) {
    return utmz.createCookieString(utmz.getFromUrl(url));
  }

  return null;
};

// eslint-disable-next-line max-statements
export const makePixelLink = (link: string, props: IMakePixelProps): string | null => {
  if (!link) {
    return null;
  }

  const {
    sub,
    type,
    source = props.isConversion ? undefined : SourceTypes.Search,
    cookies,
    pathname,
    productLink,
    isWL,
    userId,
    kind,
    productId,
    productType,
    filters,
    product,
    paymentNetwork,
  } = props || {};

  const utmzString = getUTMZString({ url: pathname, utmzCookie: cookies?.utmz });

  const patch = {
    [KEYS.SUB]: sub,
  };

  if (filters && link.indexOf(KEYS.FILTERS)) {
    patch[KEYS.FILTERS] = JSON.stringify(filters);
  }

  if (utmzString) {
    const data = [utmzString];

    if (kind === MONETIZATION_TYPES.REQUEST_LONG) {
      if (filters && productType === ProductTypes.Credits) {
        data.push(`filters=${JSON.stringify(filters)}`);
      }
      if (productId) {
        data.push(`productId=${productId}`);
      }
      if (productType) {
        data.push(`productType=${productType}`);
      }
    }

    patch[KEYS.UTM_LABELS] = data.join('|');
  }

  if (productLink) {
    const encodedLink = encodeURI(productLink);
    patch[KEYS.PRODUCT_LINK] = encodedLink;
    patch[KEYS.URL] = encodedLink;
  }

  if (product) {
    patch[KEYS.PRODUCT] = itemLabelFactory({ product, paymentNetwork });
  }

  if (pathname) {
    const [path] = pathname.split('?');

    patch[KEYS.PATH] = path;
  }

  if (userId) {
    patch[KEYS.USER_ID] = userId;
  }

  if (source) {
    patch[KEYS.SOURCE] = source;
  }

  if (type) {
    const abCookieValue = cookies.ab ? `|${cookies.ab}` : '';
    patch[KEYS.TYPE] = `${type}${abCookieValue}`;
  }

  if (cookies) {
    const yandexMetricsId = getYandexMetricsId(cookies.ym, isWL);
    const affSubGoogle = `ga_${getGaIdFromCookies(cookies.ga) || ''}`;
    const affSubYandex = yandexMetricsId ? `ym_${yandexMetricsId}` : '';
    patch[KEYS.GAID] = `${affSubGoogle}${affSubYandex ? `|${affSubYandex}` : ''}`;
  }

  return updateLinkQuery(link, patch);
};

function updateLinkQuery(link: string, query: Record<string, string>): string {
  const [url, originalQueryString = ''] = link.split('?');

  const propertiesString = qs.stringify({
    ...qs.parse(originalQueryString),
    ...query,
  });

  return `${url}?${propertiesString}`;
}

export const preparePixel = (pixel: string | undefined): string | undefined =>
  pixel && (pixel as string).replace('{timestamp}', new Date().valueOf().toString()).replace('{random}', nanoid());
